import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { FiPlus } from 'react-icons/fi';

const ButtonContainer = styled.div`
  /* Modern glassmorphism styling */
  background: rgba(99, 102, 241, 0.08);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 2px dashed rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  
  /* SAME FIXED DIMENSIONS AS MacroItem */
  width: 100%;
  height: 140px;
  min-height: 140px;
  max-height: 140px;
  
  /* Layout and spacing */
  padding: 20px 16px;
  cursor: pointer;
  text-align: center;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-sizing: border-box;
  
  /* Subtle glow effect */
  box-shadow: 
    0 8px 32px rgba(99, 102, 241, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  
  /* Smooth transitions */
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    /* Enhanced effect on hover */
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.5);
    border-style: solid;
    transform: translateY(-4px) scale(1.02);
    box-shadow: 
      0 16px 40px rgba(99, 102, 241, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }
  
  &:active {
    transform: translateY(-2px) scale(1.01);
    box-shadow: 
      0 12px 32px rgba(99, 102, 241, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }

  /* Add subtle gradient overlay */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    background: linear-gradient(135deg, 
      rgba(99, 102, 241, 0.1) 0%, 
      transparent 50%, 
      rgba(139, 92, 246, 0.1) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const IconContainer = styled.div`
  /* SAME SIZE AS MacroIconContainer */
  width: 56px;
  height: 56px;
  min-width: 56px;
  min-height: 56px;
  flex-shrink: 0;
  
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  
  /* Glass background for icon */
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(99, 102, 241, 0.25);
  
  /* Subtle inner glow */
  box-shadow: 
    inset 0 1px 0 rgba(255, 255, 255, 0.1),
    0 4px 16px rgba(99, 102, 241, 0.2);
  
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  svg {
    width: 28px;
    height: 28px;
    color: rgba(255, 255, 255, 0.9);
    stroke-width: 2.5;
  }

  ${ButtonContainer}:hover & {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(139, 92, 246, 0.25) 100%);
    border-color: rgba(99, 102, 241, 0.4);
    box-shadow: 
      inset 0 1px 0 rgba(255, 255, 255, 0.15),
      0 6px 20px rgba(99, 102, 241, 0.3);
    transform: scale(1.05);
  }
`;

const TextContainer = styled.div`
  /* SAME TEXT STYLING AS MacroNameContainer */
  font-size: 14px;
  line-height: 1.3;
  font-weight: 600;
  color: #fff;
  
  /* Fixed dimensions */
  width: 100%;
  height: 36px;
  min-height: 36px;
  max-height: 36px;
  
  padding: 0 8px;
  box-sizing: border-box;
  
  /* Center text vertically */
  display: flex;
  align-items: center;
  justify-content: center;
  
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.3px;
`;

/**
 * Component for the "Add Custom Macro" button
 */
const AddButton = ({ onClick }) => {
  return (
    <ButtonContainer onClick={onClick} title="Create a new custom macro">
      <IconContainer>
        <FiPlus />
      </IconContainer>
      <TextContainer>Create Custom Macro</TextContainer>
    </ButtonContainer>
  );
};

AddButton.propTypes = {
  onClick: PropTypes.func.isRequired
};

export default AddButton; 